import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { NAV_LINKS } from "@/lib/site";
import { Menu, X } from "lucide-react";
import logoTikratoli from "@/assets/logo-tikratoli.png";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-[#1C1913]/70 border-b border-white/5" : ""
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-4 flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
            <img
              src={logoTikratoli}
              alt="Tikratoli Estate"
              className="h-9 xl:h-10 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)] transition-transform group-hover:scale-[1.03]"
            />
            <span className="hidden md:flex flex-col leading-tight">
              <span className="font-display text-lg xl:text-xl tracking-tight text-[#F2E7D0]">Tikratoli</span>
              <span className="font-mono-label text-[9px] text-[#B8A891]">EST · 1930's</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6 min-w-0">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => {
                  // If already on this page, React Router won't fire a route change —
                  // scroll to top manually so a repeat click still returns the user to the hero.
                  if (pathname === l.to) {
                    if (window.__lenis__?.scrollTo) window.__lenis__.scrollTo(0, { immediate: true, force: true });
                    window.scrollTo(0, 0);
                  }
                }}
                className={`link-underline font-mono-label text-[#F2E7D0] hover:text-[#F2E7D0] transition ${
                  pathname === l.to ? "text-[#D97645]" : "text-[#B8A891]"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4 shrink-0">
            <Link
              to="/booking"
              data-testid="nav-book-btn"
              className="hidden sm:inline-flex items-center gap-2 border border-[#F2E7D0]/40 hover:border-[#D97645] hover:text-[#D97645] transition px-3.5 py-1.5 font-mono-label !text-[10px] text-[#F2E7D0]"
            >
              Reserve
            </Link>
            <button
              data-testid="nav-menu-toggle"
              className="xl:hidden text-[#F2E7D0]"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-40 bg-[#1C1913] pt-24 px-6 xl:hidden overflow-y-auto pb-10"
        >
          <nav className="flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="font-display text-4xl text-[#F2E7D0]"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/booking"
              data-testid="mobile-nav-book"
              className="mt-6 font-mono-label text-[#D97645] border border-[#D97645] px-6 py-3 w-fit"
            >
              Reserve a stay
            </Link>
          </nav>
        </div>
      )}
    </>
  );
}
